/** The instrument page, as pure functions.
 *
 *  Spec §8: the gateway's own node page lays a node out as one section per
 *  file family, one table per column group, one row per channel and one
 *  column per quantity. Every decision about that layout lives here, so the
 *  components stay thin and the decisions are tested without a DOM.
 */
import type {
  Cell, ChannelRow, Family, Group, Instrument, InstrumentDetail, Series,
} from '../api/types'
import type { ExportColumn } from './exportColumns'
import { relativeAge } from './time'

/** Reading families first, in the order a reader looks for them; then the
 *  node's own health; then why readings are missing. Link telemetry last,
 *  and a family nobody has named before sits between, under its own name. */
const FAMILY_ORDER = ['vw', 'volt', 'til90', 'sisgeo', 'generic-modbus',
  'gnss-data', 'gnss-stats', 'health', 'healthv3_1Sufre', 'errors']

const FAMILY_LABEL: Record<string, string> = {
  vw: 'Vibrating wire', volt: 'Voltage', til90: 'Tilt',
  sisgeo: 'Inclinometer string', 'generic-modbus': 'Weather station',
  'gnss-data': 'GNSS position', 'gnss-stats': 'GNSS statistics',
  health: 'Node health', healthv3_1Sufre: 'Node health',
  errors: 'Reading errors',
}

const LINK = 'link:'

export function familyLabel(family: string | null): string {
  if (family === null) return 'Unclassified'
  if (family.startsWith(LINK)) return `Link telemetry · ${family.slice(LINK.length)}`
  return FAMILY_LABEL[family] ?? family
}

/** Where a node with no family yet sits: after the reading families and
 *  before its health. Everything such a node reads is in that one section
 *  (Zhinvali's, while its gateway was unreachable for the structure fill),
 *  so it belongs among the readings, not after its link telemetry. */
const SECTION_ORDER: (string | null)[] = [...FAMILY_ORDER.slice(0, 7), null,
  ...FAMILY_ORDER.slice(7)]

function familyRank(family: string | null): [number, string] {
  if (family !== null && family.startsWith(LINK)) return [3, family]
  const known = SECTION_ORDER.indexOf(family)
  return known >= 0 ? [0, String(known).padStart(3, '0')] : [2, family ?? '']
}

export function orderFamilies(families: Family[]): Family[] {
  return [...families].sort((a, b) => {
    const [ra, ka] = familyRank(a.family)
    const [rb, kb] = familyRank(b.family)
    return ra - rb || ka.localeCompare(kb)
  })
}

export function instrumentLabel(
  i: Pick<Instrument, 'instrument_id' | 'name'>): string {
  return i.name ? i.name : String(i.instrument_id)
}

/** By name — the gateway's own names, TSB-1 P(1-5) … — with the node id
 *  breaking ties, and nodes the gateway has not named last. */
export function orderInstruments(instruments: Instrument[]): Instrument[] {
  return [...instruments].sort((a, b) =>
    Number(!a.name) - Number(!b.name)
    || (a.name ?? '').localeCompare(b.name ?? '', 'en', { numeric: true })
    || a.instrument_id - b.instrument_id)
}

/** The route segment for a deliberately empty selection. Without it an
 *  empty selection would read as no segment at all, which means "the
 *  default", and unticking the last sensor would tick the first one again. */
export const NO_INSTRUMENTS = 'none'

/** Which instruments the `:instrument` route segment names: one id, as every
 *  link from before multi-select reads, or a comma list. Returned in sidebar
 *  order, each once, ids this station does not have dropped. Falls back to
 *  the first instrument (by `orderInstruments`) when there is no segment, or
 *  when the segment names nothing this station has: a stale link, a
 *  hand-edited URL, or a non-numeric part, where `Number(part)` is `NaN` and
 *  matches no `instrument_id` rather than reaching `api.instrument` as a
 *  malformed request. */
export function resolveInstrumentIds(instruments: Instrument[],
                                     param: string | undefined): number[] {
  const ordered = orderInstruments(instruments)
  if (param === NO_INSTRUMENTS) return []
  const asked = new Set((param ?? '').split(',').map(Number))
  const known = ordered.filter((i) => asked.has(i.instrument_id))
    .map((i) => i.instrument_id)
  if (known.length) return known
  return ordered[0] ? [ordered[0].instrument_id] : []
}

export function instrumentsPath(code: string, ids: number[]): string {
  return `/explore/${code}/${ids.length ? ids.join(',') : NO_INSTRUMENTS}`
}

/** The selection with `id` ticked or unticked, in sidebar order. */
export function toggleInstrument(instruments: Instrument[], ids: number[],
                                 id: number): number[] {
  const next = new Set(ids)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return orderInstruments(instruments)
    .filter((i) => next.has(i.instrument_id))
    .map((i) => i.instrument_id)
}

/** The sidebar's filter: name, model or node id, ignoring case, in sidebar
 *  order. Zhinvali has 25 nodes and a reader looking for TSB-1 knows it by
 *  that name. */
export function filterInstruments(instruments: Instrument[], query: string,
                                  model = ''): Instrument[] {
  const q = query.trim().toLowerCase()
  const ordered = orderInstruments(instruments)
    .filter((i) => !model || i.model === model)
  if (!q) return ordered
  return ordered.filter((i) =>
    [i.name, i.model, String(i.instrument_id)]
      .some((field) => field?.toLowerCase().includes(q)))
}

/** The sensor types at a station, for the sidebar's "Sensor type" filter:
 *  each model the gateway reports, once, in order. Nothing is named here
 *  that the payload does not already carry. */
export function instrumentTypes(instruments: Instrument[]): string[] {
  return [...new Set(instruments.map((i) => i.model).filter((m): m is string => Boolean(m)))]
    .sort((a, b) => a.localeCompare(b))
}

export function rowLabel(
  row: Pick<ChannelRow, 'sensor_no' | 'channel_no'>): string {
  const parts: string[] = []
  if (row.sensor_no !== null) parts.push(`S${row.sensor_no}`)
  if (row.channel_no !== null) parts.push(`Ch${row.channel_no}`)
  return parts.join(' ')
}

export interface Column {
  quantity: string
  /** The unit every channel in the column shares, or null when they differ
   *  and each cell carries its own. */
  unit: string | null
  numeric: boolean
}

/** The group's columns, derived from what was provisioned, never declared.
 *
 *  A VW channel has five quantities, a Modbus sensor seventeen, a GNSS
 *  statistics family thirty-five across seven groups. A column list written
 *  down here would drop a quantity the gateway started sending tomorrow —
 *  silently, which is the failure this design exists to refuse. */
export function columnsFor(group: Group): Column[] {
  const order: string[] = []
  const units = new Map<string, Set<string>>()
  const numeric = new Map<string, boolean>()
  for (const row of group.rows) {
    for (const c of row.cells) {
      if (!units.has(c.quantity)) {
        order.push(c.quantity)
        units.set(c.quantity, new Set())
      }
      units.get(c.quantity)!.add(c.unit)
      numeric.set(c.quantity, numeric.get(c.quantity) || c.value_type === 'numeric')
    }
  }
  return order.map((quantity) => {
    const seen = [...units.get(quantity)!]
    return { quantity, unit: seen.length === 1 ? seen[0]! : null,
             numeric: numeric.get(quantity) ?? false }
  })
}

/** What tells a set of source columns apart: each column with the dash-
 *  separated tokens every one of them shares at the start and at the end
 *  taken off — `BeiDouB1-cn0-dBHz` and `GPSL1-cn0-dBHz` are `BeiDouB1` and
 *  `GPSL1`. Whole tokens only, so `Temp-173388` beside
 *  `Thermometer-temperature-Cel` is not cut to `emp-…` and `hermometer-…`.
 *  Where the shortened names would be empty or repeat, the columns
 *  themselves, which are the channel's identity within its instrument. */
export function distinguishingNames(columns: string[]): string[] {
  const split = columns.map((c) => c.split('-'))
  const shortest = Math.min(...split.map((t) => t.length))
  const same = (at: (t: string[]) => string) =>
    split.every((t) => at(t) === at(split[0]!))
  let head = 0
  while (head < shortest && same((t) => t[head]!)) head += 1
  let tail = 0
  while (tail < shortest - head && same((t) => t[t.length - 1 - tail]!)) tail += 1
  const names = split.map((t) => t.slice(head, t.length - tail).join('-'))
  const usable = names.every(Boolean) && new Set(names).size === names.length
  return usable ? names : columns
}

export interface LabelledRow {
  label: string
  cells: Cell[]
}

/** One stored row as the rows it has to be shown as.
 *
 *  A row is meant to carry one cell per quantity, and a table column is one
 *  quantity — but nothing guarantees it. Zhinvali's channels waited on a
 *  gateway outage for their family and group, so each node fell into one
 *  row: GNSS-2's three aggregations of `lat` and eight bands of `cn0` under
 *  one column each. Reading one cell per column showed the first and hid
 *  the rest. So the cells whose quantity repeats leave the row, each into a
 *  row named by what tells it from the others, and cells that share that
 *  name share the row (1Sample's lat and lon) — a name is unique within one
 *  quantity's set, so a split row still has one cell per quantity. */
export function splitRow(row: ChannelRow): LabelledRow[] {
  const base = rowLabel(row)
  const byQuantity = new Map<string, Cell[]>()
  for (const c of row.cells) {
    byQuantity.set(c.quantity, [...(byQuantity.get(c.quantity) ?? []), c])
  }
  const names = new Map<Cell, string>()
  for (const cells of byQuantity.values()) {
    if (cells.length < 2) continue
    distinguishingNames(cells.map((c) => c.source_column))
      .forEach((name, i) => names.set(cells[i]!, name))
  }
  if (!names.size) return [{ label: base, cells: row.cells }]
  const kept = row.cells.filter((c) => !names.has(c))
  const split = new Map<string, Cell[]>()
  for (const c of row.cells) {
    const name = names.get(c)
    if (name !== undefined) split.set(name, [...(split.get(name) ?? []), c])
  }
  return [
    ...(kept.length ? [{ label: base, cells: kept }] : []),
    ...[...split].map(([name, cells]) => ({
      label: [base, name].filter(Boolean).join(' · '), cells })),
  ]
}

export interface TableRow {
  /** The first cell's source column: unique within an instrument, where a
   *  label need not be. */
  key: string
  label: string
  cells: (Cell | null)[]
}

/** What a row with no sensor or channel number is: a reading of the node
 *  itself (a VW node's barometer, its battery), not of anything wired to it. */
export const NODE_ROW_LABEL = 'Node'

export function toRows(group: Group, columns: Column[]): TableRow[] {
  const rows = group.rows.flatMap(splitRow)
  // Only named beside channel rows: a table of node readings alone has no
  // Channel column, and "Node" on its one row would say nothing.
  const blank = rows.some((r) => r.label) ? NODE_ROW_LABEL : ''
  return rows.map((row) => ({
    key: row.cells[0]?.source_column ?? row.label,
    label: row.label || blank,
    cells: columns.map((col) =>
      row.cells.find((c) => c.quantity === col.quantity) ?? null),
  }))
}

/** One stored channel row of one ticked sensor, as it sits in a table that
 *  holds every ticked sensor's rows of the same type. */
export interface SensorRow {
  instrumentId: number
  instrument: string
  /** When this sensor's family last arrived: sensors report on their own
   *  cadence, so a merged table cannot carry one receipt time. */
  receivedAt: string | null
  row: ChannelRow
}

export interface SensorGroup {
  group: string | null
  rows: SensorRow[]
}

/** A type of reading across every ticked sensor: one section per family,
 *  one table per column group, a row per sensor channel. */
export interface SensorFamily {
  /** Unique per section: the family, or the node for an unclassified one. */
  key: string
  family: string | null
  label: string
  /** The newest receipt among its sensors. */
  receivedAt: string | null
  sensors: number
  groups: SensorGroup[]
}

function newer(a: string | null, b: string | null): string | null {
  if (a === null) return b
  if (b === null) return a
  return new Date(b).getTime() > new Date(a).getTime() ? b : a
}

/** Every ticked sensor's readings, grouped by what they are rather than by
 *  which sensor sent them: the piezometers' vibrating-wire channels in one
 *  table, their node health in another. Nothing here names a station or a
 *  model: the sections, tables and columns are whatever the gateways
 *  report, so a station onboarded tomorrow groups the same way.
 *
 *  A node with no family is the exception. Nothing says two unclassified
 *  nodes are the same kind of device (at Zhinvali one is a GNSS receiver
 *  and the next a piezometer string), and one table of both would run to
 *  hundreds of mostly empty columns. Each keeps a section of its own until
 *  `provision` gives it a family, and then it merges like any other. */
export function mergeFamilies(details: InstrumentDetail[]): SensorFamily[] {
  const several = details.length > 1
  const byKey = new Map<string, SensorFamily>()
  for (const detail of details) {
    const instrument = instrumentLabel(detail)
    for (const family of detail.families) {
      const key = family.family === null
        ? `unclassified:${detail.instrument_id}` : family.family
      const section = byKey.get(key) ?? {
        key, family: family.family, receivedAt: null, sensors: 0, groups: [],
        label: family.family === null && several
          ? `${familyLabel(null)} · ${instrument}` : familyLabel(family.family),
      }
      const groups = [...section.groups]
      for (const group of family.groups) {
        const rows = group.rows.map((row): SensorRow => ({
          instrumentId: detail.instrument_id, instrument,
          receivedAt: family.received_at, row }))
        const at = groups.findIndex((g) => g.group === group.group)
        if (at < 0) groups.push({ group: group.group, rows })
        else groups[at] = { ...groups[at]!, rows: [...groups[at]!.rows, ...rows] }
      }
      byKey.set(key, { ...section, groups, sensors: section.sensors + 1,
                       receivedAt: newer(section.receivedAt, family.received_at) })
    }
  }
  // Stable: unclassified sections tie on rank and keep the sensors' order.
  return [...byKey.values()].sort((a, b) => {
    const [ra, ka] = familyRank(a.family)
    const [rb, kb] = familyRank(b.family)
    return ra - rb || ka.localeCompare(kb)
  })
}

/** What a node says about itself rather than about what it measures: its
 *  health, why a reading failed, the GNSS fix quality and the radio link.
 *  Everything else is a reading, a family nobody has named yet included:
 *  it may well be one, and folding it away would hide it. */
const OTHER_FAMILIES = new Set(['health', 'healthv3_1Sufre', 'errors', 'gnss-stats'])

export function isOtherFamily(family: string | null): boolean {
  return family !== null && (OTHER_FAMILIES.has(family) || family.startsWith(LINK))
}

/** The merged sections as the page shows them: the readings, and the rest
 *  under one folded Other. Each side keeps the order it came in. */
export function splitSections(families: SensorFamily[]): {
  readings: SensorFamily[]; other: SensorFamily[]
} {
  return {
    readings: families.filter((f) => !isOtherFamily(f.family)),
    other: families.filter((f) => isOtherFamily(f.family)),
  }
}

/** What Other holds, in one line, so a reader knows whether to open it.
 *  Link telemetry is counted rather than listed: a gateway node can carry
 *  half a dozen message types and their names mean nothing at a glance. */
export function otherSummary(other: SensorFamily[]): string {
  const named = [...new Set(other.filter((f) => !f.family?.startsWith(LINK))
    .map((f) => f.label))]
  const links = other.filter((f) => f.family?.startsWith(LINK)).length
  const parts = [...named,
    ...(links === 1 ? ['link telemetry'] : links > 1 ? [`${links} link telemetry types`] : [])]
  // Sentence case after the first item, except where the word is an acronym.
  const cased = parts.map((p, i) =>
    i === 0 || /^[A-Z]{2}/.test(p) ? p : p.charAt(0).toLowerCase() + p.slice(1))
  if (cased.length <= 1) return cased.join('')
  return `${cased.slice(0, -1).join(', ')} and ${cased[cased.length - 1]}`
}

/** A quantity as the gateway names it, its underscores read as spaces:
 *  `battery_volts` is ours, `AvgPress` the gateway's, and both stay theirs. */
export function quantityLabel(quantity: string): string {
  return quantity.replace(/_/g, ' ')
}

/** One row of one sensor reads better as a set of values than as a table
 *  one line deep and seventeen columns wide. A table stays wherever rows are
 *  there to be compared: several channels, or several sensors. */
export function showsAsTiles(group: SensorGroup, several: boolean): boolean {
  return !several && toSensorRows(group, sensorColumns(group)).length === 1
}

/** A merged table's columns: every quantity any of its sensors reports. */
export function sensorColumns(group: SensorGroup): Column[] {
  return columnsFor({ group: group.group, rows: group.rows.map((r) => r.row) })
}

export interface SensorTableRow extends TableRow {
  /** The sensor's name on the first of its rows, blank on the rest, so a
   *  run of one sensor's channels reads as one block. */
  sensor: string
  runStart: boolean
  receivedAt: string | null
}

/** toRows for a merged table: each sensor's rows split as splitRow splits
 *  them, keyed by sensor and source column, since a column name is unique
 *  only within its instrument. "Node" is decided across the whole table,
 *  as toRows decides it. */
export function toSensorRows(group: SensorGroup, columns: Column[]): SensorTableRow[] {
  const split = group.rows.flatMap((r) => splitRow(r.row).map((row) => ({ r, row })))
  const blank = split.some(({ row }) => row.label) ? NODE_ROW_LABEL : ''
  return split.map(({ r, row }, i) => {
    const runStart = i === 0 || split[i - 1]!.r.instrumentId !== r.instrumentId
    return {
      key: `${r.instrumentId}:${row.cells[0]?.source_column ?? row.label}`,
      label: row.label || blank,
      sensor: runStart ? r.instrument : '',
      runStart,
      receivedAt: r.receivedAt,
      cells: columns.map((col) =>
        row.cells.find((c) => c.quantity === col.quantity) ?? null),
    }
  })
}

/** A cell's own age, when it is not its family's.
 *
 *  The family header carries one receipt time, which is right for the
 *  family and hides a channel that died inside it: at Zhinvali a thermistor
 *  can stop while its node's other channels report every hour. Two poll
 *  intervals of grace, as the latest-readings panel uses, so an hourly node
 *  is not flagged for being hourly. Words, never colour (guideline §2.6). */
export function cellNote(cell: Cell, receivedAt: string | null,
                         pollIntervalS: number,
                         now: Date = new Date()): string | null {
  if (!cell.ts) return 'no readings'
  if (!receivedAt) return null
  const behind = new Date(receivedAt).getTime() - new Date(cell.ts).getTime()
  const grace = Math.max(pollIntervalS, 60) * 2 * 1000
  return behind > grace ? relativeAge(cell.ts, now) : null
}

/** What a chart is of. The quantity alone is not enough: `lat` is in
 *  1Sample, ShortTermAgg and LongTermAgg, three different aggregations. */
export function selectionKey(family: string | null, group: string | null,
                             quantity: string): string {
  return JSON.stringify([family ?? '', group ?? '', quantity])
}

/** Every chartable cell of every instrument, instruments in the order given
 *  and each laid out in table order. */
function* numericCells(details: InstrumentDetail[]) {
  for (const detail of details) {
    for (const family of orderFamilies(detail.families)) {
      for (const group of family.groups) {
        for (const row of group.rows.flatMap(splitRow)) {
          for (const cell of row.cells) {
            if (cell.value_type === 'numeric' && cell.parameter_code) {
              yield { detail, family, group, row, cell,
                      key: selectionKey(family.family, group.group, cell.quantity) }
            }
          }
        }
      }
    }
  }
}

/** What the charts show before the reader chooses: for each instrument, the
 *  level of a reading family if there is one (the quantity a piezometer
 *  exists to measure), otherwise the first numeric quantity of its first
 *  reading family; each key once. Never health or link telemetry by
 *  default. A key is shared across instruments, so ticking three
 *  piezometers still defaults to one chart of level, not three. */
const READING_FAMILIES = new Set(FAMILY_ORDER.slice(0, 7))

export function defaultSelection(details: InstrumentDetail[]): string[] {
  const out: string[] = []
  for (const detail of details) {
    const candidates = [...numericCells([detail])].filter(({ family }) =>
      READING_FAMILIES.has(family.family ?? ''))
    const level = candidates.find(({ cell }) => cell.quantity === 'level')
    const first = level ?? candidates[0]
    if (first && !out.includes(first.key)) out.push(first.key)
  }
  return out
}

/** The parameter codes behind a selection, in table order. The wire format
 *  of /api/series and /api/export does not change: a selected quantity is
 *  simply the list of its channels' parameters, on every instrument. */
export function parametersFor(details: InstrumentDetail[], keys: string[]): string[] {
  const wanted = new Set(keys)
  const out: string[] = []
  for (const { key, cell } of numericCells(details)) {
    if (wanted.has(key) && !out.includes(cell.parameter_code!)) {
      out.push(cell.parameter_code!)
    }
  }
  return out
}

/** The export's columns for a selection: the same codes the charts show,
 *  described from the instruments' own cells so the file's headers name
 *  what the reader picked. */
export function exportColumnsFor(details: InstrumentDetail[], codes: string[],
                                 station: string): ExportColumn[] {
  const wanted = new Set(codes)
  const out: ExportColumn[] = []
  for (const { cell } of numericCells(details)) {
    if (!wanted.has(cell.parameter_code!)) continue
    if (out.some((c) => c.code === cell.parameter_code)) continue
    out.push({ station, code: cell.parameter_code!,
               display_name: cell.display_name, unit: cell.unit,
               axis_group: cell.axis_group ?? '' })
  }
  return out
}

export interface ChartGroup {
  key: string
  title: string
  /** The chart card's heading, "{quantity}, {unit}" (Instrument board), and
   *  the family and group it belongs to, the line under it. */
  heading: string
  context: string
  series: Series[]
}

interface Charted {
  key: string; label: string; title: string; column: string; quantity: string; context: string
}

/** Each line's name, never shared with another line of the same chart. A
 *  name that repeats, which splitRow should already have prevented, gives
 *  way to the source column, the channel's identity within its instrument:
 *  two lines under one name is two sensors read as one. */
function distinctLabels(lines: Charted[]): string[] {
  const count = new Map<string, number>()
  for (const l of lines) count.set(l.label, (count.get(l.label) ?? 0) + 1)
  return lines.map((l) => (count.get(l.label)! > 1 ? l.column : l.label))
}

/** A line's name: its channel (Ch1, S3 Ch4) or, where one row carried the
 *  quantity more than once, what tells the channels apart (splitRow). With
 *  several instruments on one chart the instrument leads, and stands alone
 *  for a node with a single unnamed channel: PZ-01, TSB-2 P(1-4) · Ch3. */
function lineLabel(instrument: string | null, row: string, quantity: string) {
  if (instrument === null) return row || quantity
  return [instrument, row].filter(Boolean).join(' · ')
}

/** One chart per selected quantity, one line per channel on every selected
 *  instrument carrying it. A quantity whose channels disagree on axis group
 *  is split, one chart per group, each titled with its unit: the same `p-`
 *  column is metres on a piezometer and millimetres on a settlement gauge,
 *  and one axis for both would flatten one of them into a straight line.
 *  One /api/series call carries every selected code, so every chart is cut
 *  at the same instant (rule 6). Relabelled copies; the caller's series are
 *  not touched. `several` is how many instruments are ticked, not how many
 *  have loaded, so a line is not renamed when its neighbour arrives. */
export function chartGroups(series: Series[], details: InstrumentDetail[],
                            keys: string[],
                            several = details.length > 1): ChartGroup[] {
  const byCode = new Map<string, Charted>()
  for (const { detail, key, family, group, row, cell } of numericCells(details)) {
    const where = [familyLabel(family.family), group.group, cell.quantity]
      .filter(Boolean).join(' · ')
    const instrument = several ? instrumentLabel(detail) : null
    byCode.set(cell.parameter_code!, {
      key, title: where, quantity: quantityLabel(cell.quantity),
      context: [familyLabel(family.family), group.group].filter(Boolean).join(' · '),
      label: lineLabel(instrument, row.label, cell.quantity),
      column: instrument ? `${instrument} · ${cell.source_column}` : cell.source_column,
    })
  }
  return keys.flatMap((key) => {
    const mine = series.filter((s) => byCode.get(s.parameter)?.key === key)
    const axes = [...new Set(mine.map((s) => s.axis_group))]
    return axes.map((axis) => {
      const lines = mine.filter((s) => s.axis_group === axis)
      const labels = distinctLabels(lines.map((s) => byCode.get(s.parameter)!))
      const first = byCode.get(lines[0]!.parameter)!
      const title = first.title
      const unit = lines[0]!.unit
      return {
        key: axes.length > 1 ? `${key}:${axis}` : key,
        title: axes.length > 1 ? `${title} (${unit || axis})` : title,
        heading: unit ? `${first.quantity}, ${unit}` : first.quantity,
        context: first.context,
        series: lines.map((s, i) => ({ ...s, display_name: labels[i]! })),
      }
    })
  })
}
